/**
 * Fuzzy string matching primitives — the SINGLE source of truth for approximate
 * ranking across the app (autocomplete `FuzzySelectField`, `*-ref` editors…).
 * Import from here, don't re-implement.
 *
 * Two complementary scorers live here:
 *
 *  - **`similarity`** (Levenshtein ratio, symmetric) — "user typed `serfite` and
 *    means `certificate`". Good for a single approximate spelling.
 *
 *  - **`fuzzyScore` / `fuzzyFilter`** (tiered: exact > prefix > substring >
 *    subsequence > typo, accent/case-insensitive) — the ranking used by every
 *    fuzzy `<select>`/palette in the UI. Returns -Infinity for no match, 0 for an
 *    empty query (neutral, preserves input order).
 *
 * Note: `EcaPluginPicker.jsx` keeps its OWN subsequence variant on purpose — it
 * is coupled to per-character match highlighting (`matchIndices`) and weighted
 * multi-field scoring with tuned magnitudes, which this generic scorer does not
 * provide. Do not fold it in here without porting the highlighting.
 *
 * Adapted from FuzzySuggestionField in the lutinvert-app project.
 */

/**
 * Bounded Levenshtein edit distance. Early-outs when the minimum possible
 * distance already exceeds `max` (typo tolerance on short labels without paying
 * full DP cost on long non-matches). Called with two args it computes the true
 * distance (`max` defaults to Infinity).
 *
 *   levenshtein('kitten', 'sitting')   → 3
 *   levenshtein('same', 'same')        → 0
 *
 * Case-sensitive — pre-lowercase your inputs (see {@link similarity} /
 * {@link normalizeForSearch}).
 */
export const levenshtein = (a, b, max = Infinity) => {
  if (a === b) return 0;
  const al = a.length, bl = b.length;
  if (al === 0) return bl;
  if (bl === 0) return al;
  if (Math.abs(al - bl) > max) return max + 1;
  let prev = new Array(bl + 1);
  let curr = new Array(bl + 1);
  for (let j = 0; j <= bl; j++) prev[j] = j;
  for (let i = 1; i <= al; i++) {
    curr[0] = i;
    let rowMin = curr[0];
    const ca = a.charCodeAt(i - 1);
    for (let j = 1; j <= bl; j++) {
      const cost = ca === b.charCodeAt(j - 1) ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      if (curr[j] < rowMin) rowMin = curr[j];
    }
    if (rowMin > max) return max + 1;
    [prev, curr] = [curr, prev];
  }
  return prev[bl];
};

/**
 * Normalized similarity in [0, 1] between two strings, lowercased and trimmed.
 * 1 = identical, 0 = wildly different. Early-outs when the length difference
 * exceeds 60 % of the longer string.
 *
 * Typical thresholds: >= 0.85 near-exact · >= 0.70 fuzzy · >= 0.55 long-shot.
 */
export function similarity(a, b) {
  const aN = (a || '').toLowerCase().trim();
  const bN = (b || '').toLowerCase().trim();
  if (!aN || !bN) return 0;
  const maxLen = Math.max(aN.length, bN.length);
  if (Math.abs(aN.length - bN.length) > maxLen * 0.6) return 0;
  return 1 - levenshtein(aN, bN) / maxLen;
}

/**
 * Accent- and case-insensitive normalisation for fuzzy matching.
 * "Algérie" → "algerie", so a search for "algerie" matches the accented label.
 */
export const normalizeForSearch = (s) =>
  String(s ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip combining diacritics
    .trim();

/**
 * Score two ALREADY-normalised strings. Ranking tiers, best first:
 *   exact > prefix > word-boundary substring > substring > subsequence > typo.
 * Returns -Infinity for no match.
 */
const scoreCore = (q, t) => {
  if (t === q) return 1000;
  if (t.startsWith(q)) return 900 - (t.length - q.length) * 0.5;

  const idx = t.indexOf(q);
  if (idx >= 0) {
    const atWordStart = idx === 0 || t[idx - 1] === ' ';
    return (atWordStart ? 800 : 700) - idx;
  }

  // Subsequence: every query char appears in order. Reward compact matches
  // (fewer gaps between matched characters).
  let ti = 0, gaps = 0, lastMatch = -1, matched = 0;
  for (let qi = 0; qi < q.length; qi++) {
    const c = q[qi];
    let found = -1;
    for (let k = ti; k < t.length; k++) {
      if (t[k] === c) { found = k; break; }
    }
    if (found === -1) break;
    if (lastMatch >= 0) gaps += found - lastMatch - 1;
    lastMatch = found;
    ti = found + 1;
    matched++;
  }
  if (matched === q.length) return 400 - gaps;

  // Typo tolerance: allow a bounded number of edits, scaled to query length.
  // Compare against best-fitting windows of the text (so a short query can still
  // match a long label with one typo). Disabled below 4 chars: a single edit on
  // a 2-char query matches almost anything (e.g. "al" ~ "af"ghanistan).
  const maxEdits = q.length >= 4 ? Math.floor(q.length / 4) : 0;
  if (maxEdits === 0) return -Infinity;
  let best = Infinity;
  const wlens = [q.length, q.length - 1, q.length + 1];
  for (const wlen of wlens) {
    if (wlen <= 0 || wlen > t.length) continue;
    for (let start = 0; start + wlen <= t.length; start++) {
      const d = levenshtein(q, t.substr(start, wlen), maxEdits);
      if (d < best) best = d;
      if (best === 0) break;
    }
  }
  if (best <= maxEdits) return 200 - best * 20;

  return -Infinity;
};

/**
 * Score `text` against `query`. Higher is a better match; -Infinity means no
 * match at all. An empty query passes everything through neutrally (score 0),
 * preserving the caller's original order.
 *
 * Spaces are INSIGNIFICANT: the query is also scored against a space-stripped
 * copy of both sides, best score wins. So "abcde" matches "abc de" as an exact
 * hit at the top instead of a weak subsequence.
 */
export const fuzzyScore = (query, text) => {
  const q = normalizeForSearch(query);
  const t = normalizeForSearch(text);
  if (!q) return 0;
  if (!t) return -Infinity;

  const direct = scoreCore(q, t);
  const qs = q.replace(/\s+/g, '');
  const ts = t.replace(/\s+/g, '');
  if (!qs || (qs === q && ts === t)) return direct;
  return Math.max(direct, scoreCore(qs, ts));
};

/**
 * Filter + rank `options` ([{value, label}]) against `query`. Stable: ties keep
 * the original order. An empty query returns the list untouched (optionally
 * capped by `limit`).
 */
export const fuzzyFilter = (query, options, { limit = Infinity } = {}) => {
  const list = Array.isArray(options) ? options : [];
  const q = normalizeForSearch(query);
  if (!q) return limit === Infinity ? list : list.slice(0, limit);
  const scored = [];
  for (let i = 0; i < list.length; i++) {
    const score = fuzzyScore(query, list[i]?.label);
    if (score > -Infinity) scored.push({ opt: list[i], score, i });
  }
  scored.sort((a, b) => (b.score - a.score) || (a.i - b.i));
  const out = scored.map((s) => s.opt);
  return limit === Infinity ? out : out.slice(0, limit);
};
